/**
 * BottomNav: navegación inferior fija para mobile (<md).
 *
 * Sprint 14 S14-10. El vecino y la Comisión usan portal mobile en >70%
 * de los casos. En desktop el Header naranja conserva su nav inline; en
 * mobile el nav del Header se oculta y este componente toma el rol de
 * navegación principal.
 *
 * Diseño:
 * - Fixed bottom-0 con `md:hidden` (desktop no lo ve).
 * - Fondo `bg-bg-card`, borde superior `border-t-[0.5px] border-white/[0.08]`.
 * - `safe-area-inset-bottom` para iPhones con home indicator.
 * - Cada item: icono 22px arriba + label 10-11px abajo, altura 56px.
 * - Activo: ícono y label en `text-orange` + barrita superior `bg-orange`.
 * - Inactivo: `text-white/55`.
 * - `aria-current="page"` en el activo.
 *
 * El conjunto de items se decide igual que el Header (vista vecino vs
 * vista comisión, vía `vistaActualPorPath`) y se filtra por roles del
 * usuario.
 */
import { Link, useLocation } from 'react-router-dom';
import {
  ROLES_ADMIN,
  ROLES_PUBLICAN_CONTENIDO,
  ROLES_REVISAN_SOLICITUDES,
  ROLES_VEN_REPROGRAMACION,
  ROLES_VEN_SUSPENSIONES,
  tieneAlgunRol,
} from '../../lib/auth/roles';
import type { AuthMeResponse } from '../../lib/api/types';
import { vistaActualPorPath } from '../../lib/layout/vista';

interface BottomNavProps {
  me: AuthMeResponse;
  /** Override del pathname para previews dev-only (S14-10 ShellPreview).
   *  Default: usa `useLocation().pathname` del router externo. */
  pathnameOverride?: string;
}

interface BottomNavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  visible: boolean;
  /** Match end-only para la raíz `/vecino` (evita que `/vecino/foo` también
   *  marque Inicio como activo). */
  exact?: boolean;
}

/* ===== Iconos SVG inline ===== */

const ICON_PROPS = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function IconHome() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 11 L12 3 L21 11" />
      <path d="M5 10 V20 H10 V14 H14 V20 H19 V10" />
    </svg>
  );
}

function IconWallet() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10 H21" />
      <circle cx="16.5" cy="14.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21 C4 16 8 14 12 14 C16 14 20 16 20 21" />
    </svg>
  );
}

function IconDocument() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M14 3 H6 V21 H18 V7 Z" />
      <path d="M14 3 V7 H18" />
      <path d="M8 12 H16 M8 16 H14" />
    </svg>
  );
}

function IconMegaphone() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 10 V14 H7 L15 19 V5 L7 10 Z" />
      <path d="M18 9 C19.5 10.5 19.5 13.5 18 15" />
    </svg>
  );
}

function IconInbox() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 13 L6 5 H18 L21 13 V19 H3 Z" />
      <path d="M3 13 H8 L9.5 15.5 H14.5 L16 13 H21" />
    </svg>
  );
}

function IconShieldCheck() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 3 L20 6 V12 C20 17 16 20 12 21 C8 20 4 17 4 12 V6 Z" />
      <path d="M9 12 L11 14 L15 10" />
    </svg>
  );
}

function IconBuilding() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M5 21 V5 H19 V21" />
      <path d="M5 21 H19" />
      <path d="M9 9 H11 M13 9 H15 M9 13 H11 M13 13 H15 M9 17 H11 M13 17 H15" />
    </svg>
  );
}

function IconBan() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6 L18.4 18.4" />
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M4 12 A8 8 0 0 1 18 7 L20 9" />
      <path d="M20 4 V9 H15" />
      <path d="M20 12 A8 8 0 0 1 6 17 L4 15" />
      <path d="M4 20 V15 H9" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="11" cy="11" r="7" />
      <path d="M16 16 L21 21" />
    </svg>
  );
}

function IconChart() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M4 20 V4" />
      <path d="M4 20 H20" />
      <rect x="7" y="12" width="3" height="5" />
      <rect x="12" y="8" width="3" height="9" />
      <rect x="17" y="5" width="3" height="12" />
    </svg>
  );
}

/* ===== Helpers de match de ruta ===== */

function rutaActiva(pathname: string, to: string, exact?: boolean): boolean {
  if (exact) return pathname === to;
  return pathname === to || pathname.startsWith(to + '/');
}

/* ===== Componente principal ===== */

export function BottomNav({ me, pathnameOverride }: BottomNavProps) {
  const location = useLocation();
  const pathname = pathnameOverride ?? location.pathname;
  const enVistaAdmin = vistaActualPorPath(pathname) === 'comision';

  const itemsVecino: BottomNavItem[] = [
    { to: '/vecino', label: 'Inicio', icon: <IconHome />, visible: true, exact: true },
    { to: '/vecino/estado-cuenta', label: 'Mi cuenta', icon: <IconWallet />, visible: true },
    { to: '/vecino/mis-datos', label: 'Mis datos', icon: <IconUser />, visible: true },
    { to: '/vecino/documentos', label: 'Documentos', icon: <IconDocument />, visible: true },
    { to: '/vecino/avisos', label: 'Avisos', icon: <IconMegaphone />, visible: true },
  ];

  const itemsAdmin: BottomNavItem[] = [
    {
      to: '/admin/solicitudes-acceso',
      label: 'Solicitudes',
      icon: <IconInbox />,
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_REVISAN_SOLICITUDES),
    },
    {
      to: '/admin/validaciones',
      label: 'Validaciones',
      icon: <IconShieldCheck />,
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_REVISAN_SOLICITUDES),
    },
    {
      to: '/admin/documentos',
      label: 'Documentos',
      icon: <IconDocument />,
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_PUBLICAN_CONTENIDO),
    },
    {
      to: '/admin/avisos',
      label: 'Avisos',
      icon: <IconMegaphone />,
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_PUBLICAN_CONTENIDO),
    },
    {
      to: '/admin/tenencias',
      label: 'Tenencias',
      icon: <IconBuilding />,
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_ADMIN),
    },
    {
      to: '/admin/consulta-vecinos',
      label: 'Vecinos',
      icon: <IconSearch />,
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_ADMIN),
    },
    {
      to: '/admin/suspensiones',
      label: 'Suspensiones',
      icon: <IconBan />,
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_VEN_SUSPENSIONES),
    },
    {
      to: '/admin/reprogramacion',
      label: 'Reprogramación',
      icon: <IconRefresh />,
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_VEN_REPROGRAMACION),
    },
    {
      to: '/admin/reportes',
      label: 'Reportes',
      icon: <IconChart />,
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_ADMIN),
    },
  ];

  const items = (enVistaAdmin ? itemsAdmin : itemsVecino).filter((i) => i.visible);
  if (items.length === 0) return null;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-bg-card border-t-[0.5px] border-white/[0.08] md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      aria-label="Navegación principal"
    >
      <ul className="flex items-stretch justify-around">
        {items.map((item) => {
          const activa = rutaActiva(pathname, item.to, item.exact);
          return (
            <li key={item.to} className="flex-1 min-w-0">
              <Link
                to={item.to}
                aria-current={activa ? 'page' : undefined}
                className={`relative flex flex-col items-center justify-center gap-1 py-2 h-14 transition-colors duration-base ${
                  activa ? 'text-orange' : 'text-white/55 hover:text-white/80'
                }`}
              >
                {activa && (
                  <span
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-8 bg-orange rounded-b-sm"
                  />
                )}
                {item.icon}
                <span className="text-[10px] leading-none font-medium truncate max-w-full px-1">
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
